import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';

interface StakingDepositRow {
  id: string;
  userId: string;
  username: string;
  amount: number;
  packageMonths: number;
  monthlyRate: number;
  totalEarned: number;
  status: string;
  startedAt: string;
  endsAt: string;
  daysRemaining: number;
}

interface StakingStats {
  totalStakers: number;
  activeDeposits: number;
  totalStaked: number;
  totalEarned: number;
  totalStakingBalance: number;
  byPackage: { packageMonths: number; count: number; totalAmount: number }[];
}

type FilterStatus = 'all' | 'active' | 'completed';
type AdminTab = 'witon' | 'usdt';

export function AdminStaking() {
  const [tab, setTab] = useState<AdminTab>('witon');
  const [deposits, setDeposits] = useState<StakingDepositRow[]>([]);
  const [stats, setStats] = useState<StakingStats | null>(null);
  const [usdtDeposits, setUsdtDeposits] = useState<StakingDepositRow[]>([]);
  const [usdtStats, setUsdtStats] = useState<StakingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [exchangeRate, setExchangeRate] = useState('');
  const [savingRate, setSavingRate] = useState(false);

  const [modUserId, setModUserId] = useState('');
  const [modAmount, setModAmount] = useState('');
  const [modReason, setModReason] = useState('');
  const [modifying, setModifying] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [depositsRes, statsRes, usdtDepositsRes, usdtStatsRes] = await Promise.all([
        api.getAdminStakingDeposits(filter),
        api.getAdminStakingStats(),
        api.getAdminUsdtStakingDeposits(filter),
        api.getAdminUsdtStakingStats(),
      ]);
      setDeposits(depositsRes.data.deposits || []);
      setStats(statsRes.data);
      setUsdtDeposits(usdtDepositsRes.data.deposits || []);
      setUsdtStats(usdtStatsRes.data);
      if (statsRes.data.exchangeRate) {
        setExchangeRate(statsRes.data.exchangeRate.toString());
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveRate = async () => {
    if (!exchangeRate || savingRate) return;
    setSavingRate(true);
    try {
      await api.updateStakingConfig('witon_to_usdt_rate', exchangeRate);
      alert('Exchange rate updated!');
    } catch (e: any) {
      alert(e.response?.data?.message || 'Failed');
    } finally {
      setSavingRate(false);
    }
  };

  const handleModifyBalance = async () => {
    if (!modUserId || !modAmount || !modReason || modifying) return;
    setModifying(true);
    try {
      if (tab === 'usdt') {
        await api.modifyUserUsdtStakingBalance(modUserId, Number(modAmount), modReason);
      } else {
        await api.modifyUserStakingBalance(modUserId, Number(modAmount), modReason);
      }
      alert(`${tab === 'usdt' ? 'USDT' : 'WiTon'} Staking balance modified!`);
      setModUserId('');
      setModAmount('');
      setModReason('');
      fetchData();
    } catch (e: any) {
      alert(e.response?.data?.message || 'Failed');
    } finally {
      setModifying(false);
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'completed': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const PACKAGE_COLORS: Record<number, string> = { 3: '#10b981', 6: '#3b82f6', 12: '#8b5cf6', 36: '#f59e0b', 60: '#ef4444' };
  const cardStyle = { background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '14px', border: '1px solid rgba(255,255,255,0.06)' } as React.CSSProperties;
  const inputStyle = { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '10px', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box' as const } as React.CSSProperties;

  const isUsdt = tab === 'usdt';
  const activeDeposits = isUsdt ? usdtDeposits : deposits;
  const activeStats = isUsdt ? usdtStats : stats;
  const currency = isUsdt ? 'USDT' : 'WiTon';
  const fmtAmt = (n: number) => isUsdt ? `$${n.toFixed(2)}` : Math.floor(n).toLocaleString();

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)', color: '#fff', padding: '16px' }}>
      {/* Tab Toggle */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '4px' }}>
        <button onClick={() => setTab('witon')} style={{
          flex: 1, padding: '10px', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
          background: tab === 'witon' ? 'rgba(16,185,129,0.2)' : 'transparent',
          color: tab === 'witon' ? '#10b981' : 'rgba(255,255,255,0.5)',
        }}>WiTon Staking</button>
        <button onClick={() => setTab('usdt')} style={{
          flex: 1, padding: '10px', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
          background: tab === 'usdt' ? 'rgba(45,125,50,0.2)' : 'transparent',
          color: tab === 'usdt' ? '#2e7d32' : 'rgba(255,255,255,0.5)',
        }}>USDT Staking</button>
      </div>

      {/* Stats */}
      {activeStats && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
          <div style={cardStyle}>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Stakers</div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#3b82f6' }}>{activeStats.totalStakers}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Active Deposits</div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#10b981' }}>{activeStats.activeDeposits}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Total Staked</div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#f59e0b' }}>{fmtAmt(activeStats.totalStaked)}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Total Earned</div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#10b981' }}>{fmtAmt(activeStats.totalEarned)}</div>
          </div>
          <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px' }}>Total Staking Balances</div>
            <div style={{ fontSize: '20px', fontWeight: 800 }}>{fmtAmt(activeStats.totalStakingBalance)} {currency}</div>
          </div>
          {activeStats.byPackage.length > 0 && (
            <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px' }}>By Package</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {activeStats.byPackage.map(p => (
                  <div key={p.packageMonths} style={{ padding: '6px 10px', borderRadius: '8px', background: `${PACKAGE_COLORS[p.packageMonths] || '#888'}18`, border: `1px solid ${PACKAGE_COLORS[p.packageMonths] || '#888'}44` }}>
                    <span style={{ fontWeight: 700, color: PACKAGE_COLORS[p.packageMonths] || '#888' }}>{p.packageMonths}M</span>
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginLeft: '6px' }}>{p.count} dep / {fmtAmt(p.totalAmount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Exchange Rate Config — WiTon only */}
      {!isUsdt && (
        <div style={{ ...cardStyle, marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>WiTon → USDT Rate</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input value={exchangeRate} onChange={e => setExchangeRate(e.target.value)} placeholder="0.01" style={{ ...inputStyle, flex: 1 }} />
            <button onClick={handleSaveRate} disabled={savingRate} style={{ padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: savingRate ? 0.5 : 1 }}>
              {savingRate ? '...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Modify Staking Balance */}
      <div style={{ ...cardStyle, marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>Modify {currency} Staking Balance</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <input value={modUserId} onChange={e => setModUserId(e.target.value)} placeholder="User ID" style={inputStyle} />
          <input type="number" value={modAmount} onChange={e => setModAmount(e.target.value)} placeholder="Amount (+/-)" style={inputStyle} />
          <input value={modReason} onChange={e => setModReason(e.target.value)} placeholder="Reason" style={inputStyle} />
          <button onClick={handleModifyBalance} disabled={modifying || !modUserId || !modAmount || !modReason} style={{ padding: '10px', borderRadius: '8px', border: 'none', background: '#f59e0b', color: '#000', fontWeight: 700, cursor: 'pointer', opacity: modifying ? 0.5 : 1 }}>
            {modifying ? 'Processing...' : `Modify ${currency} Balance`}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
        {(['all', 'active', 'completed'] as FilterStatus[]).map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '6px 14px', borderRadius: '20px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
            background: filter === f ? '#3b82f6' : 'rgba(255,255,255,0.08)', color: filter === f ? '#fff' : 'rgba(255,255,255,0.6)',
          }}>
            {f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Completed'}
          </button>
        ))}
      </div>

      {/* Deposits List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.4)' }}>Loading...</div>
      ) : activeDeposits.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.4)' }}>No {currency} staking deposits found</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {activeDeposits.map(d => {
            const color = PACKAGE_COLORS[d.packageMonths] || '#888';
            return (
              <div key={d.id} style={{ ...cardStyle, borderColor: `${color}33` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <div>
                    <span style={{ fontWeight: 700, color }}>{d.packageMonths}M</span>
                    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginLeft: '8px' }}>{d.monthlyRate}%/mo</span>
                  </div>
                  <span style={{ fontSize: '11px', color: getStatusColor(d.status), padding: '2px 8px', borderRadius: '10px', background: `${getStatusColor(d.status)}18` }}>
                    {d.status}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px' }}>
                  @{d.username} <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>({d.userId.substring(0, 8)}...)</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '2px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)' }}>Amount</span>
                  <span style={{ fontWeight: 700 }}>{fmtAmt(d.amount)} {currency}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '2px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)' }}>Earned</span>
                  <span style={{ fontWeight: 700, color: '#10b981' }}>{fmtAmt(d.totalEarned)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)' }}>Period</span>
                  <span style={{ fontSize: '11px' }}>{formatDate(d.startedAt)} — {formatDate(d.endsAt)}{d.status === 'active' ? ` (${d.daysRemaining}d)` : ''}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
